import { Video, Wifi, MapPin, Settings, Shield, Users, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import UserSelector from './UserSelector'

const AdvancedCameraForm = ({
    formData,
    handleChange,
    handleLocationChange,
    handleResolutionChange,
    handleRoleChange,
    handleUsersChange,
    roles,
    selectedUsers,
    showUserAssignment,
    setShowUserAssignment,
    errors
}) => {
    const [showPassword, setShowPassword] = useState(false)
    
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Video className="h-5 w-5 mr-2" />
                        Basic Information
                    </h3>

                    <div className="space-y-4">
                        {/* Camera Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Camera Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
                                placeholder="Enter camera name"
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={3}
                                className="input w-full"
                                placeholder="Enter camera description"
                            />
                        </div>

                        {/* Company / Manufacturer - drives URL format (Hikvision vs Dahua) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Company / Manufacturer *
                            </label>
                            <select
                                name="manufacturer"
                                value={formData.manufacturer || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    handleChange(e);
                                    if (val === 'Dahua') {
                                        handleChange({ target: { name: 'url', value: '/cam/realmonitor' } });
                                        if (!formData.trackNumber || formData.trackNumber > 99) handleChange({ target: { name: 'trackNumber', value: 1 } });
                                    } else if (val === 'CP Plus') {
                                        handleChange({ target: { name: 'url', value: '/cam/realmonitor' } });
                                        if (!formData.trackNumber || formData.trackNumber > 99) handleChange({ target: { name: 'trackNumber', value: 1 } });
                                        if (formData.dahuaSubtype === undefined || formData.dahuaSubtype === null) handleChange({ target: { name: 'dahuaSubtype', value: 1 } });
                                    } else if (val === 'Hikvision') {
                                        handleChange({ target: { name: 'url', value: '/Streaming/Channels/101' } });
                                        if (!formData.trackNumber || formData.trackNumber < 100) handleChange({ target: { name: 'trackNumber', value: 1001 } });
                                    }
                                }}
                                className="input w-full"
                            >
                                <option value="">Select company...</option>
                                <option value="Hikvision">Hikvision</option>
                                <option value="Dahua">Dahua</option>
                                <option value="CP Plus">CP Plus (NVR)</option>
                                <option value="Other">Other (generic RTSP)</option>
                            </select>
                            <p className="text-gray-500 text-xs mt-1">
                                URL path is built automatically for Hikvision, Dahua and CP Plus
                            </p>
                        </div>

                        {/* Dahua path type - try if device returns 404 for standard path */}
                        {formData.manufacturer === 'Dahua' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dahua path type
                                    </label>
                                    <select
                                        name="dahuaPathType"
                                        value={formData.dahuaPathType || 'standard'}
                                        onChange={handleChange}
                                        className="input w-full"
                                    >
                                        <option value="standard">Standard (/cam/realmonitor?channel=X&amp;subtype=X)</option>
                                        <option value="onvif">With Onvif (+ unicast=true&amp;proto=Onvif)</option>
                                        <option value="live">Legacy /live (single stream)</option>
                                        <option value="custom">Custom (use URL path below)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dahua stream type
                                    </label>
                                    <select
                                        name="dahuaSubtype"
                                        value={formData.dahuaSubtype !== undefined && formData.dahuaSubtype !== null ? formData.dahuaSubtype : 0}
                                        onChange={(e) => handleChange({ target: { name: 'dahuaSubtype', value: parseInt(e.target.value, 10) } })}
                                        className="input w-full"
                                    >
                                        <option value={0}>Main stream (subtype=0, higher resolution)</option>
                                        <option value={1}>Sub stream (subtype=1, lower resolution – often works when main returns 404)</option>
                                    </select>
                                    <p className="text-gray-500 text-xs mt-1">
                                        Use Sub stream if main stream returns 404
                                    </p>
                                </div>
                            </>
                        )}

                        {/* CP Plus NVR: channel + sub stream (same URL format as Dahua) */}
                        {formData.manufacturer === 'CP Plus' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Channel (1-based)
                                    </label>
                                    <input
                                        type="number"
                                        name="trackNumber"
                                        value={formData.trackNumber ?? 1}
                                        onChange={handleChange}
                                        className="input w-full"
                                        placeholder="1"
                                        min="1"
                                    />
                                    <p className="text-gray-500 text-xs mt-1">
                                        NVR channel number (1, 2, 3…)
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Stream type
                                    </label>
                                    <select
                                        name="dahuaSubtype"
                                        value={formData.dahuaSubtype !== undefined && formData.dahuaSubtype !== null ? formData.dahuaSubtype : 1}
                                        onChange={(e) => handleChange({ target: { name: 'dahuaSubtype', value: parseInt(e.target.value, 10) } })}
                                        className="input w-full"
                                    >
                                        <option value={0}>Main stream (subtype=0)</option>
                                        <option value={1}>Sub stream (subtype=1)</option>
                                    </select>
                                    <p className="text-gray-500 text-xs mt-1">
                                        Sub stream recommended for NVR: rtsp://.../cam/realmonitor?channel=1&subtype=1
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Camera Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Camera Type *
                            </label>
                            <select
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="input w-full"
                            >
                                <option value="rtsp">RTSP</option>
                                <option value="isapi">ISAPI (Hikvision)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Connection Settings */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Wifi className="h-5 w-5 mr-2" />
                        Connection Settings
                    </h3>

                    <div className="space-y-4">
                        {/* IP Address */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                IP Address/Hostname *
                            </label>
                            <input
                                type="text"
                                name="ipAddress"
                                value={formData.ipAddress}
                                onChange={handleChange}
                                className={`input w-full ${errors.ipAddress ? 'border-red-500' : ''}`}
                                placeholder="192.168.1.100"
                            />
                            {errors.ipAddress && (
                                <p className="text-red-500 text-xs mt-1">{errors.ipAddress}</p>
                            )}
                        </div>

                        {/* Port */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Port *
                            </label>
                            <input
                                type="number"
                                name="port"
                                value={formData.port}
                                onChange={handleChange}
                                className={`input w-full ${errors.port ? 'border-red-500' : ''}`}
                                placeholder="554"
                                min="1"
                                max="65535"
                            />
                            {errors.port && (
                                <p className="text-red-500 text-xs mt-1">{errors.port}</p>
                            )}
                        </div>

                        {/* URL Path - auto for Hikvision/Dahua, manual for Other */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                URL Path *
                            </label>
                            <input
                                type="text"
                                name="url"
                                value={formData.url}
                                onChange={handleChange}
                                className={`input w-full ${errors.url ? 'border-red-500' : ''}`}
                                placeholder={formData.manufacturer === 'Dahua' || formData.manufacturer === 'CP Plus' ? '/cam/realmonitor' : formData.manufacturer === 'Hikvision' ? '/Streaming/Channels/101' : '/live/av0'}
                            />
                            {formData.manufacturer === 'Dahua' && (
                                <p className="text-gray-500 text-xs mt-1">
                                    Dahua: path is /cam/realmonitor; channel set by Track/Channel below
                                </p>
                            )}
                            {formData.manufacturer === 'CP Plus' && (
                                <p className="text-gray-500 text-xs mt-1">
                                    CP Plus NVR: path is /cam/realmonitor; channel and subtype set above
                                </p>
                            )}
                            {formData.manufacturer === 'Hikvision' && (
                                <p className="text-gray-500 text-xs mt-1">
                                    Hikvision: e.g. /Streaming/Channels/101, 102, 201…
                                </p>
                            )}
                            {errors.url && (
                                <p className="text-red-500 text-xs mt-1">{errors.url}</p>
                            )}
                        </div>

                        {/* Track / Channel Number - Hikvision: 1001+; Dahua/CP Plus: 1-based channel */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {formData.manufacturer === 'Dahua' || formData.manufacturer === 'CP Plus' ? 'Channel (1-based)' : 'Track Number'}
                            </label>
                            <input
                                type="number"
                                name="trackNumber"
                                value={formData.trackNumber ?? (formData.manufacturer === 'Dahua' || formData.manufacturer === 'CP Plus' ? 1 : 1001)}
                                onChange={handleChange}
                                className={`input w-full ${errors.trackNumber ? 'border-red-500' : ''}`}
                                placeholder={formData.manufacturer === 'Dahua' || formData.manufacturer === 'CP Plus' ? '1' : '1001'}
                                min="1"
                                max="9999"
                            />
                            <p className="text-gray-500 text-xs mt-1">
                                {formData.manufacturer === 'Dahua'
                                    ? 'Dahua: channel 1, 2, 3… (main stream)'
                                    : formData.manufacturer === 'CP Plus'
                                    ? 'CP Plus NVR: channel 1, 2, 3…'
                                    : 'Hikvision: track 1001, 1002, etc.'}
                            </p>
                            {errors.trackNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.trackNumber}</p>
                            )}
                        </div>

                        {/* Username */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Username *
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className={`input w-full ${errors.username ? 'border-red-500' : ''}`}
                                placeholder="admin"
                            />
                            {errors.username && (
                                <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Password *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`input w-full pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                    placeholder="Enter password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
                {/* Location Information */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <MapPin className="h-5 w-5 mr-2" />
                        Location Information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                State/Province
                            </label>
                            <input
                                type="text"
                                value={formData.location.state}
                                onChange={(e) => handleLocationChange('state', e.target.value)}
                                className="input w-full"
                                placeholder="State"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                City
                            </label>
                            <input
                                type="text"
                                value={formData.location.city}
                                onChange={(e) => handleLocationChange('city', e.target.value)}
                                className="input w-full"
                                placeholder="City"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Exact Location
                            </label>
                            <input
                                type="text"
                                value={formData.location.exactLocation}
                                onChange={(e) => handleLocationChange('exactLocation', e.target.value)}
                                className="input w-full"
                                placeholder="Building/Room"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coordinates
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Latitude
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.coordinates.latitude}
                                        onChange={(e) => handleLocationChange('latitude', e.target.value)}
                                        className="input w-full"
                                        placeholder="22.994079"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Longitude
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.coordinates.longitude}
                                        onChange={(e) => handleLocationChange('longitude', e.target.value)}
                                        className="input w-full"
                                        placeholder="72.544628"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Camera Specifications */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Settings className="h-5 w-5 mr-2" />
                        Camera Specifications
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Manufacturer
                            </label>
                            <input
                                type="text"
                                name="manufacturer"
                                value={formData.manufacturer}
                                onChange={handleChange}
                                className="input w-full"
                                placeholder="Set by Company above (Hikvision, Dahua, etc.)"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Model
                            </label>
                            <input
                                type="text"
                                name="model"
                                value={formData.model}
                                onChange={handleChange}
                                className="input w-full"
                                placeholder="DS-2CD2142FWD-I"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Resolution Width
                            </label>
                            <input
                                type="number"
                                value={formData.resolution.width}
                                onChange={(e) => handleResolutionChange('width', parseInt(e.target.value))}
                                className={`input w-full ${errors.resolutionWidth ? 'border-red-500' : ''}`}
                                placeholder="1920"
                                min="320"
                                max="7680"
                            />
                            {errors.resolutionWidth && (
                                <p className="text-red-500 text-xs mt-1">{errors.resolutionWidth}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Resolution Height
                            </label>
                            <input
                                type="number"
                                value={formData.resolution.height}
                                onChange={(e) => handleResolutionChange('height', parseInt(e.target.value))}
                                className={`input w-full ${errors.resolutionHeight ? 'border-red-500' : ''}`}
                                placeholder="1080"
                                min="240"
                                max="4320"
                            />
                            {errors.resolutionHeight && (
                                <p className="text-red-500 text-xs mt-1">{errors.resolutionHeight}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                FPS
                            </label>
                            <input
                                type="number"
                                name="fps"
                                value={formData.fps}
                                onChange={handleChange}
                                className={`input w-full ${errors.fps ? 'border-red-500' : ''}`}
                                placeholder="25"
                                min="1"
                                max="60"
                            />
                            {errors.fps && (
                                <p className="text-red-500 text-xs mt-1">{errors.fps}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bitrate (kbps)
                            </label>
                            <input
                                type="number"
                                name="bitrate"
                                value={formData.bitrate}
                                onChange={handleChange}
                                className={`input w-full ${errors.bitrate ? 'border-red-500' : ''}`}
                                placeholder="2048"
                                min="64"
                                max="8192"
                            />
                            {errors.bitrate && (
                                <p className="text-red-500 text-xs mt-1">{errors.bitrate}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Access Control */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Shield className="h-5 w-5 mr-2" />
                        Access Control
                    </h3>

                    <div className="space-y-4">
                        {/* Role Assignment */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assign to Roles
                            </label>
                            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                {roles.map((role) => (
                                    <label key={role._id} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.accessRoles.includes(role._id)}
                                            onChange={() => handleRoleChange(role._id)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-gray-700">{role.name}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.accessRoles && (
                                <p className="text-red-500 text-xs mt-1">{errors.accessRoles}</p>
                            )}
                        </div>

                        {/* User Assignment Toggle */}
                        <div className="border-t border-gray-200 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowUserAssignment(!showUserAssignment)}
                                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                <Users className="h-4 w-4" />
                                <span className="font-medium">
                                    {showUserAssignment ? 'Hide' : 'Show'} User Assignment
                                </span>
                            </button>
                        </div>

                        {/* User Assignment */}
                        {showUserAssignment && (
                            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="mb-3">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        Assign to Specific Users
                                    </h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Select specific users who can access this camera.
                                        Users can also access through their assigned roles.
                                    </p>
                                </div>
                                <UserSelector
                                    selectedUsers={selectedUsers}
                                    onUsersChange={handleUsersChange}
                                    placeholder="Search and select users..."
                                    maxUsers={10}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdvancedCameraForm 